import { ApolloProvider, useMutation } from '@apollo/client';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { NameInput } from 'sr-types/lib/production/v1/graphql';
import { ContactInfoInput } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import DataList from '../common/list/DataList';
import ListView from '../common/list/ListView';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import KeyPeopleTile from './KeyPeopleTile';
import { ContactWithKey, extractNames, getKeyPeopleInput } from './helpers/peopleUtils';
import { addPersonToProduction, client } from './helpers/production';
import { useSaveKeyPeople } from './helpers/productionApi';

interface PersonWithDepartment extends ContactWithKey {
    department: string;
}

type ListProps = {
    id: string;
    loading: boolean;
    data: any;
    entity: string;
    feature: string;
    productionId: string;
    peopleWithKey: ContactWithKey[];
    setSearchAllPeople: Dispatch<SetStateAction<boolean>>;
    setIsCreate: Dispatch<SetStateAction<boolean>>;
    setQuery: Dispatch<SetStateAction<string>>;
    [key: string]: any;
};

const List = ({
    id,
    loading,
    data,
    entity,
    feature,
    productionId,
    setIsCreate,
    peopleWithKey,
    setSearchAllPeople,
    setQuery,
    ...props
}: ListProps) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [isButtonLoading, setIsButtonLoading] = useState({ isSaving: false, person: undefined });
    const { saveKeyPeople } = useSaveKeyPeople();
    const [savePersonToProduction] = useMutation(addPersonToProduction, { client });

    const filteredData = useMemo(() => {
        const associatedPeopleIds = peopleWithKey.map((p) => p?.personReference?.id);
        if (data?.results?.hits?.items) {
            return data.results.hits.items
                .filter((person) => !associatedPeopleIds.includes(person?.reference?.id))
                .map((p) => {
                    const { reference, name, ...otherFields } = p;
                    return { ...otherFields, personReference: reference, name: extractNames(name) };
                });
        }
        return [];
    }, [data, peopleWithKey]);

    return (
        <DataList
            {...props}
            id={id}
            loading={loading}
            feature={feature}
            pagination={data?.results?.hits?.page}
            items={filteredData}
            ItemRenderer={(props) => {
                return (
                    <KeyPeopleTile
                        {...props}
                        showNoRole={false}
                        isLoading={isButtonLoading}
                        onMarkClick={(person) => {
                            setIsButtonLoading({ isSaving: true, person });
                            const variables = {
                                productionId,
                                input: {
                                    new: {
                                        contact: {
                                            contactInfo: person.contactInfo as ContactInfoInput,
                                            personReference: person.personReference,
                                            name: person.name as NameInput,
                                            productionReference: person.productionReference,
                                            production: person.production,
                                            organization: person.organization,
                                            title: person.title,
                                            organizationDepartment: (person as PersonWithDepartment).department,
                                            role: person.role,
                                            productionRole: person.productionRole,
                                            productionDepartment: person.productionDepartment,
                                            roles: person.role && person.role.trim() !== '' ? [person.role] : [],
                                            productionRoles:
                                                person.productionRole && person.productionRole.trim() !== ''
                                                    ? [person.productionRole]
                                                    : []
                                        },
                                        isHired: false
                                    }
                                }
                            };
                            savePersonToProduction({
                                variables: variables,
                                context: { headers: { ownerId: activeOrganizationAccount } }
                            })
                                .then(() => {
                                    saveKeyPeople(
                                        productionId,
                                        getKeyPeopleInput(peopleWithKey, undefined, undefined, {
                                            ...variables.input.new?.contact,
                                            isKey: true
                                        }),
                                        true
                                    )
                                        .then(() => {
                                            setTimeout(() => {
                                                client.refetchQueries({ include: ['Production'] }).finally(() => {
                                                    setIsButtonLoading({ isSaving: false, person: undefined });
                                                    setSearchAllPeople(false);
                                                    setQuery('');
                                                });
                                            }, 300);
                                        })
                                        .catch(() => {
                                            setIsButtonLoading({ isSaving: false, person: undefined });
                                        });
                                })
                                .catch(() => {
                                    setIsButtonLoading({ isSaving: false, person: undefined });
                                });
                        }}
                    />
                );
            }}
            disableRipple
            onClick={() => {}}
        />
    );
};

type KeyPeopleListViewProps = {
    query: string;
    productionId: string;
    peopleWithKey: ContactWithKey[];
    setSearchAllPeople: Dispatch<SetStateAction<boolean>>;
    setIsCreate: Dispatch<SetStateAction<boolean>>;
    setQuery: Dispatch<SetStateAction<string>>;
};

export default function KeyPeopleListView({
    query,
    productionId,
    peopleWithKey,
    setSearchAllPeople,
    setIsCreate,
    setQuery
}: KeyPeopleListViewProps) {
    const searchkit = useMemo(() => new SearchkitClient(), []);

    const renderPeopleList = useCallback(
        (props) => (
            <List
                {...props}
                peopleWithKey={peopleWithKey}
                productionId={productionId}
                setSearchAllPeople={setSearchAllPeople}
                setIsCreate={setIsCreate}
                setQuery={setQuery}
            />
        ),
        [peopleWithKey, productionId, setSearchAllPeople, setIsCreate, setQuery]
    );

    return (
        <ApolloProvider client={searchClient}>
            <SearchkitProvider client={searchkit}>
                <ListView
                    id="account_people"
                    entity={ENTITY_CONSTANTS.person}
                    feature={FEATURE_CONSTANTS.peopleResources}
                    uri={undefined}
                    query={slimResultsQuery(ENTITY_CONSTANTS.person)}
                    EntityListRenderer={renderPeopleList}
                    showBreadCrumbs={false}
                    containerSx={{ height: '100%', m: 0, p: 1 }}
                    facetsVisibility={{
                        filters: false,
                        searchPanel: true,
                        promotedFacets: false,
                        quickFilter: false
                    }}
                    initialSearchQuery={query}
                />
            </SearchkitProvider>
        </ApolloProvider>
    );
}
